import { useEffect, useState } from 'react'
import { Wifi, WifiOff } from 'lucide-react'
import { useConnectionStatus } from '@/hooks/useConnectionStatus'
import './ConnectionStatusIndicator.css'

export const ConnectionStatusIndicator = () => {
  const { isOnline } = useConnectionStatus()
  const [shouldRender, setShouldRender] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [status, setStatus] = useState<'online' | 'offline'>('online')

  // Global click interceptor and body class application
  useEffect(() => {
    if (!isOnline) {
      document.body.classList.add('app-offline')
      
      const handleGlobalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        const interactive = target.closest('button, a, input[type="submit"], [role="button"]')
        if (interactive && !interactive.classList.contains('allow-offline')) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
      
      window.addEventListener('click', handleGlobalClick, true)
      return () => {
        window.removeEventListener('click', handleGlobalClick, true)
      }
    } else {
      document.body.classList.remove('app-offline')
    }
  }, [isOnline])

  useEffect(() => {
    if (!isOnline) {
      setStatus('offline')
      setShouldRender(true)
      setIsFadingOut(false)
    } else {
      if (shouldRender) {
        setStatus('online')
        setIsFadingOut(false)
        
        const fadeTimeout = setTimeout(() => {
          setIsFadingOut(true)
        }, 3000)

        const removeTimeout = setTimeout(() => {
          setShouldRender(false)
          setIsFadingOut(false)
        }, 3500)

        return () => {
          clearTimeout(fadeTimeout)
          clearTimeout(removeTimeout)
        }
      }
    }
  }, [isOnline, shouldRender])

  if (!shouldRender) return null

  const isOfflineMode = status === 'offline'

  return (
    <div
      className={`connection-status-toast ${isOfflineMode ? 'offline' : 'online'} ${
        isFadingOut ? 'fade-out' : ''
      }`}
    >
      <div className="connection-toast-icon-container">
        {isOfflineMode ? (
          <WifiOff size={20} className="connection-toast-icon pulse" />
        ) : (
          <Wifi size={20} className="connection-toast-icon bounce" />
        )}
      </div>
      <div className="connection-toast-text-container">
        <h4 className="connection-toast-title">
          {isOfflineMode ? 'Sem conexão com a internet' : 'Conexão restabelecida!'}
        </h4>
        <p className="connection-toast-message">
          {isOfflineMode
            ? 'Por favor, verifique a sua conectividade local.'
            : 'Sua conexão voltou a funcionar com sucesso.'}
        </p>
      </div>
    </div>
  )
}
